<section class="pt-16 pb-2 px-6 md:px-10 lg:px-20 text-center">
  <div class="max-w-4xl mx-auto">
    <h1 class="text-2xl md:text-3xl font-bold text-[#212529] mb-4">
      Discover the Power of <span class="text-[#2A64F9]">MoneySeel</span>
    </h1>
    <p class="text-base text-[#6C757D]">
      From smart receipt capture to real-time dashboards and effortless bill
      splitting, explore how MoneySeel helps you save time, stay organized, and
      take control of your finances—across every device.
    </p>
  </div>
</section>

<section
  id="feature-receipt-capture"
  class="bg-white py-16 px-6 md:px-10 lg:px-20"
>
  <div
    class="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center"
    data-aos="fade-up"
  >
    <!-- Left Image -->
    <div class="flex justify-center">
      <img
        src="/images/Feature_Instant_Invoice_Receipt_Capture.png"
        alt="Receipt Capture"
        class="w-full max-w-md rounded shadow-lg"
      />
    </div>

    <!-- Right Content -->
    <div>
      <h2 class="text-2xl font-bold text-[#2A64F9] mb-3">
        Instant Invoice & Receipt Capture
      </h2>
      <p class="text-base text-[#6C757D] mb-6">
        Transform Paper Chaos into Digital Clarity in Seconds. Never manually
        enter receipt data again!
      </p>

      <div class="space-y-4 mb-6">
        <h3 class="font-semibold text-[#212529]">How it Works:</h3>
        <ul class="list-disc list-inside text-[#212529] text-sm space-y-2">
          <li>
            <strong>Snap a Photo:</strong> Use your smartphone camera in the
            MoneySeel app.
          </li>
          <li>
            <strong>Upload from Gallery:</strong> Select an existing photo from
            your device.
          </li>
          <li>
            <strong>Upload Documents:</strong> Upload PDFs, JPEGs, or PNGs
            through web or mobile.
          </li>
        </ul>
      </div>

      <div
        class="bg-[#E9F7EF] border-l-4 border-[#28A745] p-4 rounded-lg shadow-sm mb-6"
      >
        <h4 class="font-semibold text-[#212529] mb-2">
          AI Magic - What gets extracted:
        </h4>
        <div
          class="grid grid-cols-2 gap-2 text-sm text-[#212529] leading-relaxed"
        >
          <span>✔ Vendor Name</span>
          <span>✔ Transaction Date & Time</span>
          <span>✔ Total Amount</span>
          <span>✔ Currency</span>
          <span>✔ Tax Amounts</span>
          <span>✔ Payment Method</span>
        </div>
      </div>

      <!-- <div
        class="bg-[#E9F7EF] border-l-4 border-[#28A745] p-4 rounded-lg shadow-sm"
      >
        <h4 class="font-semibold text-[#28A745] mb-1">Mini Use Case</h4>
        <p class="text-sm text-[#212529] leading-relaxed">
          Imagine finishing a business lunch. Snap the receipt before leaving
          the table. By the time you're back at your desk, the expense is
          logged, data extracted, and ready for review.
        </p>
      </div> -->

      <div class="mb-6">
        <h3 class="font-semibold text-[#212529] mb-2">Benefits:</h3>
        <ul class="list-disc list-inside text-sm text-[#212529] space-y-1">
          <li>⏱️ Time-Saving: No more manual entry</li>
          <li>✅ Accuracy: Reduces human error</li>
          <li>📂 Organization: Auto-filed and searchable receipts</li>
          <li>🧾 Tax-Ready: Clean, audit-proof records</li>
          <li>🧠 Smart Storage: Never lose a receipt again</li>
        </ul>
      </div>
    </div>
  </div>
</section>

<section
  id="feature-bill-splitting"
  class="bg-white py-16 px-6 md:px-10 lg:px-20"
>
  <div
    class="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center"
    data-aos="fade-up"
  >
    <!-- Left: Textual Explanation -->
    <div>
      <h2 class="text-2xl font-bold text-[#28A745] mb-3">
        Effortless Group Bill Splitting
      </h2>
      <p class="text-base text-[#6C757D] mb-6">
        Simplify Shared Expenses. Keep track of who paid what, effortlessly and
        fairly.
      </p>

      <div class="text-sm text-[#212529] space-y-4 mb-6">
        <p>
          <strong>Create Shared Groups:</strong> Set up expense groups for
          trips, households, or events and invite others to join.
        </p>
        <p>
          <strong>Track Shared Expenses:</strong> Group members can add
          receipts, specify who paid, and categorize them easily.
        </p>
        <p>
          <strong>Real-Time Balances:</strong> Instantly view who owes what.
          MoneySeel calculates and updates everything automatically.
        </p>
        <p>
          <strong>Transparent & Fair:</strong> Shared views ensure all
          participants stay informed and disputes are avoided.
        </p>
      </div>

      <div class="mb-6">
        <h3 class="font-semibold text-[#212529] mb-2">Benefits:</h3>
        <ul class="list-disc list-inside text-sm text-[#212529] space-y-1">
          <li>🤝 Reduces Awkward Conversations</li>
          <li>📊 Fairness & Transparency for All</li>
          <li>⏳ Saves Time: No spreadsheets or manual math</li>
          <li>🌍 Perfect for Trips, Roommates & Families</li>
        </ul>
      </div>

      <!-- <div
        class="bg-[#FFF3CD] border-l-4 border-yellow-500 p-4 rounded-lg shadow-sm"
      >
        <h4 class="font-semibold text-yellow-700 mb-1">Mini Use Case</h4>
        <p class="text-sm text-[#212529] leading-relaxed">
          You and three friends go on a weekend trip. Each person logs what they
          spent in the shared group. At the end, MoneySeel calculates exactly
          who owes whom—no awkward follow-ups or guesswork.
        </p>
      </div> -->
    </div>

    <!-- Right: Illustration -->
    <div class="flex justify-center">
      <img
        src="/images/Feature_Group_Bill_Splitting.png"
        alt="Group Bill Splitting"
        class="w-full max-w-md rounded shadow-lg"
      />
    </div>
  </div>
</section>

<section id="feature-sync" class="bg-white py-16 px-6 md:px-10 lg:px-20">
  <div
    class="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center"
    data-aos="fade-up"
  >
    <!-- Left: Illustration -->
    <div class="flex justify-center">
      <img
        src="/images/Feature_Multi_Platform_Sync_Accessibility.png"
        alt="Multi-Device Sync"
        class="w-full max-w-md rounded shadow-lg"
      />
    </div>

    <!-- Right: Explanation Content -->
    <div>
      <h2 class="text-2xl font-bold text-[#2A64F9] mb-3">
        Multi-Platform Sync & Accessibility
      </h2>
      <p class="text-base text-[#6C757D] mb-6">
        Your Finances, Synced and Secure, Wherever Life Takes You. Access
        MoneySeel on your terms.
      </p>

      <div class="text-sm text-[#212529] space-y-4 mb-6">
        <p>
          <strong>Flawless Cross-Device Experience:</strong> Available on iOS,
          Android, and web—track expenses on the go or at your desk.
        </p>
        <p>
          <strong>Real-Time Synchronization:</strong> Entries update instantly
          across all devices. No syncing hassles, no version mismatches.
        </p>
        <p>
          <strong>Offline Capability:</strong> Even if you're offline, your
          entries will sync automatically once you're reconnected.
        </p>
      </div>

      <div class="mb-6">
        <h3 class="font-semibold text-[#212529] mb-2">Benefits:</h3>
        <ul class="list-disc list-inside text-sm text-[#212529] space-y-1">
          <li>📱 Ultimate Convenience: Capture expenses anytime, anywhere</li>
          <li>🔄 Always Up-to-Date: Real-time consistency across platforms</li>
          <li>
            💻 Flexibility: Use mobile for quick capture, web for deep analysis
          </li>
          <li>
            🔐 Peace of Mind: Your data is always backed up and accessible
          </li>
        </ul>
      </div>

      <!-- <div
        class="bg-[#E3F2FD] border-l-4 border-[#2A64F9] p-4 rounded-lg shadow-sm"
      >
        <h4 class="font-semibold text-[#2A64F9] mb-1">Mini Use Case</h4>
        <p class="text-sm text-[#212529] leading-relaxed">
          Snap a receipt for coffee on your iPhone. Later, review it on your
          desktop at work—already categorized and synced. That’s MoneySeel’s
          seamless sync in action.
        </p>
      </div> -->
    </div>
  </div>
</section>

<section
  id="feature-categorization"
  class="bg-white py-16 px-6 md:px-10 lg:px-20"
>
  <div
    class="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center"
    data-aos="fade-up"
  >
    <!-- Left: Explanation Content -->
    <div>
      <h2 class="text-2xl font-bold text-[#28A745] mb-3">
        Smart Categorization & Tagging
      </h2>
      <p class="text-base text-[#6C757D] mb-6">
        Understand Your Spending Intuitively. Let MoneySeel do the heavy lifting
        of organizing your finances.
      </p>

      <div class="space-y-5 mb-6 text-[#212529] text-sm">
        <p>
          <strong>Automatic Assignment:</strong> Our system suggests relevant
          categories using vendor names, keywords, and your past habits.
        </p>
        <p>
          <strong>Full Customization & Control:</strong> You can create, edit,
          and manage your own categories to match your financial structure.
        </p>
        <p>
          <strong>Tagging for Deeper Insights:</strong> Add tags like “Project
          Alpha” or “Client Meeting” for powerful filtering and analytics.
        </p>
      </div>

      <div class="mb-6">
        <h3 class="font-semibold text-[#212529] mb-2">Benefits:</h3>
        <ul class="list-disc list-inside text-sm text-[#212529] space-y-1">
          <li>📁 Effortless Organization</li>
          <li>🔍 Clear Spending Insights</li>
          <li>📊 Improved Budgeting Accuracy</li>
          <li>🧩 Personalized Category System</li>
        </ul>
      </div>

      <!-- <div
        class="bg-[#FFF3CD] border-l-4 border-yellow-500 p-4 rounded-lg shadow-sm"
      >
        <h4 class="font-semibold text-yellow-700 mb-1">Mini Use Case</h4>
        <p class="text-sm text-[#212529] leading-relaxed">
          You pay your monthly electricity bill. MoneySeel’s OCR detects the
          vendor and automatically files it under “Utilities.” No sorting
          required - your budget stays perfectly updated.
        </p>
      </div> -->
    </div>

    <!-- Right: Image -->
    <div class="flex justify-center">
      <img
        src="/images/Feature_Smart_Categorization_Tagging.png"
        alt="Smart Categorization"
        class="w-full max-w-md rounded shadow-lg"
      />
    </div>
  </div>
</section>
<section id="feature-dashboard" class="bg-white py-16 px-6 md:px-10 lg:px-20">
  <div
    class="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center"
    data-aos="fade-up"
  >
    <!-- Left: Illustration -->
    <div class="flex justify-center">
      <img
        src="/images/Feature_Dynamic_Expense_Views_Intuitive_Dashboard.png"
        alt="Dashboard View Illustration"
        class="w-full max-w-md rounded shadow-lg"
      />
    </div>

    <!-- Right: Explanation -->
    <div>
      <h2 class="text-2xl font-bold text-[#2A64F9] mb-3">
        Dynamic Expense Views & Intuitive Dashboard
      </h2>
      <p class="text-base text-[#6C757D] mb-6">
        Your Financial Big Picture, Beautifully Visualized. Transform raw data
        into actionable understanding.
      </p>

      <div class="text-[#212529] text-sm space-y-4 mb-6">
        <p>
          <strong>Comprehensive Dashboard:</strong> Quickly access personal,
          business, and group expense summaries, totals, and recent activity.
        </p>
        <p>
          <strong>Flexible Views:</strong> Filter spending by date ranges,
          categories, tags, and payment methods for complete clarity.
        </p>
        <p>
          <strong>Insightful Charts:</strong> Gain insights from pie charts and
          bar graphs that show where your money goes and how it changes over
          time.
        </p>
      </div>

      <div class="mb-6">
        <h3 class="font-semibold text-[#212529] mb-2">Benefits:</h3>
        <ul class="list-disc list-inside text-sm text-[#212529] space-y-1">
          <li>📈 Instant Clarity: Visualize your financial health with ease</li>
          <li>
            🔎 Identify Trends: Spot patterns and seasonal shifts in spending
          </li>
          <li>🧠 Informed Decisions: Base your budgeting on real data</li>
          <li>🎯 Motivation: Track progress on savings and goals visually</li>
        </ul>
      </div>

      <!-- <div
        class="bg-[#E3F2FD] border-l-4 border-[#2A64F9] p-4 rounded-lg shadow-sm"
      >
        <h4 class="font-semibold text-[#2A64F9] mb-1">Mini Use Case</h4>
        <p class="text-sm text-[#212529] leading-relaxed">
          Want to see how much your business spent on marketing last quarter?
          Just a few clicks on the MoneySeel dashboard - filter by date and
          category - and you'll have a clear visual report at your fingertips.
        </p>
      </div> -->
    </div>
  </div>
</section>
<section id="feature-export" class="bg-white py-16 px-6 md:px-10 lg:px-20">
  <div
    class="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center"
    data-aos="fade-up"
  >
    <!-- Left: Text Explanation -->
    <div>
      <h2 class="text-2xl font-bold text-[#28A745] mb-3">
        Flawless Data Export & Reporting
      </h2>
      <p class="text-base text-[#6C757D] mb-6">
        Your Data, Your Rules. Access and share your financial information with
        unparalleled ease.
      </p>

      <div class="text-sm text-[#212529] space-y-4 mb-6">
        <p>
          <strong>Flexible Export Options:</strong> Export your entire expense
          history in universal formats like <strong>CSV</strong> and
          <strong>PDF</strong>—ideal for accountants, spreadsheets, or audits.
        </p>
        <p>
          <strong>Customizable Reports:</strong> Select specific date ranges,
          categories, tags, or user accounts to tailor the output.
        </p>
        <p>
          <strong>Easy Sharing:</strong> Send reports directly to accountants or
          save them securely for personal or business use—no manual formatting
          needed.
        </p>
      </div>

      <div class="mb-6">
        <h3 class="font-semibold text-[#212529] mb-2">Benefits:</h3>
        <ul class="list-disc list-inside text-sm text-[#212529] space-y-1">
          <li>🧾 Tax Time Simplified: Ready-to-go, clean financial records</li>
          <li>📤 Professional Reporting: Create polished reports in a click</li>
          <li>🔐 Data Ownership: Export, store, and use your data freely</li>
          <li>
            🤝 Smooth Collaboration: Share updates with stakeholders instantly
          </li>
        </ul>
      </div>

      <!-- <div
        class="bg-[#FFF3CD] border-l-4 border-yellow-500 p-4 rounded-lg shadow-sm"
      >
        <h4 class="font-semibold text-yellow-700 mb-1">Mini Use Case</h4>
        <p class="text-sm text-[#212529] leading-relaxed">
          It's year-end. Instead of scrambling through files, export a full CSV
          report from MoneySeel and send it straight to your accountant—clean,
          fast, and error-free.
        </p>
      </div> -->
    </div>

    <!-- Right: Illustration -->
    <div class="flex justify-center">
      <img
        src="/images/Feature_Seamless_Data_Export_Reporting.png"
        alt="Export Reporting"
        class="w-full max-w-md rounded shadow-lg"
      />
    </div>
  </div>
</section>
